import {
    NEVER_TRANSLATE_SELECTOR,
    SKIP_TEXT_SELECTOR,
    TRANSLATABLE_ATTRIBUTES,
} from '../constants/translationConfig';

/**
 * Finds the pieces of a page that should be translated.
 *
 * A "target" is { kind: 'text' | 'attr', node, element, attr?, text }:
 *   - kind 'text': a text node       (node = the Text node, element = its parent element)
 *   - kind 'attr': an attribute      (node = element = the element, attr = 'placeholder' | 'title')
 * `text` is the value at the moment of collection; `element` is what gets observed for scrolling.
 */

const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const URL_PATTERN = /^(https?:\/\/|www\.)\S+$/i;
const LATIN_LETTER = /[A-Za-z]/;

const ATTRIBUTE_SELECTOR = TRANSLATABLE_ATTRIBUTES.map((attr) => `[${attr}]`).join(',');

/**
 * The page is written in English, so only text with at least one Latin letter is sent. This also
 * leaves out numbers, symbols, emails, links and text that is already in another language.
 */
export const isTranslatableText = (text) => {
    if (typeof text !== 'string') return false;
    const trimmed = text.trim();
    return (
        trimmed.length > 0 &&
        LATIN_LETTER.test(trimmed) &&
        !EMAIL_PATTERN.test(trimmed) &&
        !URL_PATTERN.test(trimmed)
    );
};

const isInsideNeverTranslate = (element) =>
    !element || element.closest(NEVER_TRANSLATE_SELECTOR) !== null;

const toTextTarget = (textNode) => {
    const element = textNode.parentElement;
    if (!element) return null;
    if (isInsideNeverTranslate(element) || element.closest(SKIP_TEXT_SELECTOR)) return null;
    if (!isTranslatableText(textNode.nodeValue)) return null;
    return { kind: 'text', node: textNode, element, text: textNode.nodeValue };
};

/** Translatable attributes (placeholder, title) of one element. */
export const collectAttributeTargets = (element) => {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) return [];
    if (isInsideNeverTranslate(element)) return [];
    const targets = [];
    TRANSLATABLE_ATTRIBUTES.forEach((attr) => {
        const value = element.getAttribute(attr);
        if (isTranslatableText(value)) {
            targets.push({ kind: 'attr', node: element, element, attr, text: value });
        }
    });
    return targets;
};

/** Everything translatable at or below `rootNode` (a Text node or an Element). */
export const collectTextTargets = (rootNode) => {
    if (!rootNode) return [];

    if (rootNode.nodeType === Node.TEXT_NODE) {
        const target = toTextTarget(rootNode);
        return target ? [target] : [];
    }
    if (rootNode.nodeType !== Node.ELEMENT_NODE) return [];
    if (isInsideNeverTranslate(rootNode)) return [];

    const targets = [];
    const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
        const target = toTextTarget(current);
        if (target) targets.push(target);
        current = walker.nextNode();
    }

    targets.push(...collectAttributeTargets(rootNode));
    rootNode.querySelectorAll(ATTRIBUTE_SELECTOR).forEach((element) => {
        targets.push(...collectAttributeTargets(element));
    });
    return targets;
};
